/**
 * 语义色（Semantic Tokens）
 * 使用 baseLight/baseDark 组合出「字体/图标/填充/描边/遮罩/投影/功能色」等语义，与 semantic-tokens.json 对应
 */
import { baseLight, baseDark } from './base-tokens';

/** 语义 token 对：[浅色主题值, 深色主题值] */
export type SemanticTokenPair = [light: string, dark: string];

export type SemanticTokens = {
  // --- 字体 (Font)，对应 JSON「字体」---
  textLight: SemanticTokenPair; // 浅色字体 → 辅助色-100
  textDark: SemanticTokenPair; // 深色字体 → 辅助色-800
  textSecondary: SemanticTokenPair; // 深色字体-次要文本 → 辅助色-800-50
  textPrimary100: SemanticTokenPair; // 主题色-100
  textPrimary200: SemanticTokenPair; // 主题色-200
  textPrimary300: SemanticTokenPair; // 主题色-300
  textNeutral500: SemanticTokenPair; // 辅助色-500
  textNeutral600: SemanticTokenPair; // 辅助色-600

  // --- 图标/按钮 (Icon/Button)，对应 JSON「图标-按钮」---
  iconPrimary100: SemanticTokenPair; // 主题色-100
  iconPrimary200: SemanticTokenPair; // 主题色-200
  iconPrimary300: SemanticTokenPair; // 主题色-300
  iconNeutral100: SemanticTokenPair; // 辅助色-100
  iconNeutral200: SemanticTokenPair; // 辅助色-200
  iconNeutral300: SemanticTokenPair; // 辅助色-300
  iconNeutral500: SemanticTokenPair; // 辅助色-500
  iconNeutral600: SemanticTokenPair; // 辅助色-600
  iconNeutral700: SemanticTokenPair; // 辅助色-700
  iconNeutral800: SemanticTokenPair; // 辅助色-800
  iconNeutral800A50: SemanticTokenPair; // 辅助色-800-50

  // --- 填充 (Fill)，对应 JSON「填充」---
  fillNeutral100: SemanticTokenPair; // 辅助色-100
  fillNeutral200: SemanticTokenPair; // 辅助色-200
  fillPrimary200: SemanticTokenPair; // 主题色-200
  fillPrimary300: SemanticTokenPair; // 主题色-300
  fillNeutral600: SemanticTokenPair; // 辅助色-600（JSON 中「主题色-600」实际指向辅助色-600）
  fillNeutral700: SemanticTokenPair; // 辅助色-700
  fillNeutral800: SemanticTokenPair; // 辅助色-800

  // --- 描边 (Stroke)，对应 JSON「描边」---
  strokePrimary100: SemanticTokenPair; // 主题色-100
  strokePrimary200: SemanticTokenPair; // 主题色-200
  strokePrimary300: SemanticTokenPair; // 主题色-300
  strokeNeutral100: SemanticTokenPair; // 辅助色-100
  strokeNeutral800: SemanticTokenPair; // 辅助色-800
  strokeNeutral800A25: SemanticTokenPair; // 辅助色-800-25

  // --- 遮罩 (Overlay)，对应 JSON「遮罩」---
  overlay100A50: SemanticTokenPair; // 100-50 → 辅助色-100-50
  overlay800A25: SemanticTokenPair; // 800-25 → 辅助色-800-25
  overlay800A50: SemanticTokenPair; // 800-50 → 辅助色-800-50

  // --- 投影 (Shadow)，对应 JSON「投影」---
  shadow25: SemanticTokenPair; // 800-25
  shadow50: SemanticTokenPair; // 800-50

  // --- 功能颜色 (Functional Colors)，对应 JSON「功能颜色」---
  funcError: SemanticTokenPair; // Error
  funcSuccess: SemanticTokenPair; // Success
  funcInfo: SemanticTokenPair; // Info
  funcSp1: SemanticTokenPair; // SP1
  funcSp2: SemanticTokenPair; // SP2
  funcSp3: SemanticTokenPair; // SP3
  funcSp4: SemanticTokenPair; // SP4
  funcSp5: SemanticTokenPair; // SP5
};

/** 语义 token 实例：由 baseLight/baseDark 推导，与 semantic-tokens.json 结构一致 */
export const semanticTokens: SemanticTokens = {
  // --- 字体 (Font) ---
  textLight: [baseLight.neutral100, baseDark.neutral100],
  textDark: [baseLight.neutral800, baseDark.neutral800],
  textSecondary: [baseLight.neutral800A50, baseDark.neutral800A50],
  textPrimary100: [baseLight.primary100, baseDark.primary100],
  textPrimary200: [baseLight.primary200, baseDark.primary200],
  textPrimary300: [baseLight.primary300, baseDark.primary300],
  textNeutral500: [baseLight.neutral500, baseDark.neutral500],
  textNeutral600: [baseLight.neutral600, baseDark.neutral600],

  // --- 图标/按钮 (Icon/Button) ---
  iconPrimary100: [baseLight.primary100, baseDark.primary100],
  iconPrimary200: [baseLight.primary200, baseDark.primary200],
  iconPrimary300: [baseLight.primary300, baseDark.primary300],
  iconNeutral100: [baseLight.neutral100, baseDark.neutral100],
  iconNeutral200: [baseLight.neutral200, baseDark.neutral200],
  iconNeutral300: [baseLight.neutral300, baseDark.neutral300],
  iconNeutral500: [baseLight.neutral500, baseDark.neutral500],
  iconNeutral600: [baseLight.neutral600, baseDark.neutral600],
  iconNeutral700: [baseLight.neutral700, baseDark.neutral700],
  iconNeutral800: [baseLight.neutral800, baseDark.neutral800],
  iconNeutral800A50: [baseLight.neutral800A50, baseDark.neutral800A50],

  // --- 填充 (Fill) ---
  fillNeutral100: [baseLight.neutral100, baseDark.neutral100],
  fillNeutral200: [baseLight.neutral200, baseDark.neutral200],
  fillPrimary200: [baseLight.primary200, baseDark.primary200],
  fillPrimary300: [baseLight.primary300, baseDark.primary300],
  fillNeutral600: [baseLight.neutral600, baseDark.neutral600],
  fillNeutral700: [baseLight.neutral700, baseDark.neutral700],
  fillNeutral800: [baseLight.neutral800, baseDark.neutral800],

  // --- 描边 (Stroke) ---
  strokePrimary100: [baseLight.primary100, baseDark.primary100],
  strokePrimary200: [baseLight.primary200, baseDark.primary200],
  strokePrimary300: [baseLight.primary300, baseDark.primary300],
  strokeNeutral100: [baseLight.neutral100, baseDark.neutral100],
  strokeNeutral800: [baseLight.neutral800, baseDark.neutral800],
  strokeNeutral800A25: [baseLight.neutral800A25, baseDark.neutral800A25],

  // --- 遮罩 (Overlay) ---
  overlay100A50: [baseLight.neutral100A50, baseDark.neutral100A50],
  overlay800A25: [baseLight.neutral800A25, baseDark.neutral800A25],
  overlay800A50: [baseLight.neutral800A50, baseDark.neutral800A50],

  // --- 投影 (Shadow) ---
  shadow25: [baseLight.neutral800A25, baseDark.neutral800A25],
  shadow50: [baseLight.neutral800A50, baseDark.neutral800A50],

  // --- 功能颜色 (Functional Colors) ---
  funcError: [baseLight.error, baseDark.error],
  funcSuccess: [baseLight.success, baseDark.success],
  funcInfo: [baseLight.info, baseDark.info],
  funcSp1: [baseLight.sp1, baseDark.sp1],
  funcSp2: [baseLight.sp2, baseDark.sp2],
  funcSp3: [baseLight.sp3, baseDark.sp3],
  funcSp4: [baseLight.sp4, baseDark.sp4],
  funcSp5: [baseLight.sp5, baseDark.sp5],
};
